import React from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';

import ProductTable from '@nerdwallet/react-product-table';

import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import Row from '../row/row';
import ReviewAnnotation from '../shared-table-components/review-annotation';
import EduDetailsSection from './edu-details-section';

import createInvestingConfig from './investing-config';
import createEduConfig from './edu-config';
import { stringInputSpec, stringSelectSpec } from '../../lib/editing-configs';
import SegmentImpression from '../segment-impression';

import './expanding-product-table.less';
import { segmentImpressionId } from '../../lib/segment-analytics';
import { addCtaApplyNowLinkTracking } from '../../lib/utils';

const customComponents = {
  ReviewAnnotation,
};

const productsToDisplay = (products, ids) =>
  ids.reduce((accum, el) => {
    const foundProduct = products[el];

    if (foundProduct) {
      accum.push(foundProduct);
    }

    return accum;
  }, []);

// maps table type to the config files & details sections, which are product-specific
const configGenerators = {
  edu: createEduConfig,
  investing: createInvestingConfig,
};
const detailsSections = {
  edu: EduDetailsSection,
};

/*
 * Product table that displays brokers from redux state, filtered & ordered by ids prop.
 */
const ExpandingProductTable = ({
  allProducts,
  ids,
  name,
  ctaColumnTitle,
  ctaCopy,
  ctaIcon,
  getDefaultCta,
  type,
}) => {
  const products = productsToDisplay(allProducts, ids);
  const generateConfig = get(configGenerators, type);
  const config =
    generateConfig &&
    generateConfig({
      ctaCopy: ctaCopy || getDefaultCta(),
      ctaColumnTitle,
      showIcon: ctaIcon === 'lock',
    });
  const detailsSection = get(detailsSections, type);
  const { pageViewId } = useAnalyticsPageView();

  const productsWithSegment = products?.map((product) => ({
    ...product,
    ...addCtaApplyNowLinkTracking(product, segmentImpressionId, pageViewId),
  }));

  return (
    <Row className="expanding-product-table">
      <SegmentImpression
        eventName="ExpandingProductTable Viewed"
        eventProps={{
          impression_id: segmentImpressionId,
          section_name: 'expanding_product_table',
          pageview_id: pageViewId,
        }}
      >
        <ProductTable
          products={productsWithSegment}
          tableName={name}
          tableConfig={config}
          customComponents={customComponents}
          detailsComponent={detailsSection}
        />
      </SegmentImpression>
    </Row>
  );
};

ExpandingProductTable.propTypes = {
  ids: PropTypes.array.isRequired,
  allProducts: PropTypes.object.isRequired,
  name: PropTypes.string,
  type: PropTypes.string,
  ctaCopy: PropTypes.string,
  getDefaultCta: PropTypes.func,
  ctaColumnTitle: PropTypes.string,
  mediaQuery: PropTypes.string,
  ctaIcon: PropTypes.oneOf(['none', 'lock']),
};

ExpandingProductTable.defaultProps = {
  ids: [],
  allProducts: {},
  name: 'unnamed',
  ctaIcon: 'none',
  type: 'investing',
};

ExpandingProductTable.getEditingConfig = () => [
  stringInputSpec('ctaColumnTitle', 'CTA column title'),
  stringSelectSpec('ctaIcon', 'Icon', [
    { label: 'None', value: 'none' },
    { label: 'Lock', value: 'lock' },
  ]),
];

export default ExpandingProductTable;
